import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Building2, User as UserIcon, Mail, MapPin, Phone,
  Loader2, CheckCircle2, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { MultiPhoneEditor } from '../components/MultiPhoneEditor';
import { MultiAddressEditor } from '../components/MultiAddressEditor';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { FolderTree } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = (contactType = 'customer') => ({
  name: '',
  email: '',
  contact_type: contactType,
  entity_type: 'individual',
  phones: [],
  addresses: [],
  payment_terms: 30,
  credit_limit: '',
  apply_template_id: '',  // optional template for the auto-root folder
});

export default function ContactForm() {
  const navigate = useNavigate();
  const { contactId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  const isEdit = Boolean(contactId);

  // For new contacts, default type comes from ?type=customer|vendor query
  const initialType = (searchParams.get('type') === 'vendor') ? 'vendor' : 'customer';
  const [form, setForm] = useState(emptyForm(initialType));
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    // Load available folder templates for the optional auto-apply dropdown
    if (isEdit || !currentWorkspace) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/folder-templates?workspace_id=${currentWorkspace.workspace_id}&include_presets=true`);
        if (res.ok) setTemplates(await res.json());
      } catch {}
    })();
  }, [isEdit, currentWorkspace]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/contacts/${contactId}`);
        if (res.ok) {
          const c = await res.json();
          setForm({
            name: c.name || '',
            email: c.email || '',
            contact_type: c.contact_type || 'customer',
            entity_type: c.entity_type || 'individual',
            phones: c.phones || [],
            addresses: c.addresses || [],
            payment_terms: c.payment_terms ?? 30,
            credit_limit: c.credit_limit ?? '',
          });
        } else {
          toast.error('Contact not found');
          navigate('/contacts');
        }
      } catch {
        toast.error('Failed to load contact');
      } finally {
        setLoading(false);
      }
    })();
  }, [contactId, isEdit, navigate]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        entity_type: form.entity_type,
        phones: (form.phones || []).map(p => ({ ...p, phone_id: p.phone_id?.startsWith('tmp_') ? undefined : p.phone_id })),
        addresses: (form.addresses || []).map(a => ({ ...a, address_id: a.address_id?.startsWith('tmp_') ? undefined : a.address_id })),
        payment_terms: parseInt(form.payment_terms) || 30,
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
      };
      // For new contacts include contact_type + optional template
      if (!isEdit) {
        payload.contact_type = form.contact_type;
        if (form.apply_template_id) payload.apply_template_id = form.apply_template_id;
      }

      const url = isEdit
        ? `${API_URL}/api/contacts/${contactId}`
        : `${API_URL}/api/contacts?workspace_id=${currentWorkspace.workspace_id}`;
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json();
        toast.success(isEdit ? 'Contact updated' : 'Contact created');
        navigate(isEdit ? `/contacts/${contactId}` : `/contacts/${saved.contact_id}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (isEdit) navigate(`/contacts/${contactId}`);
    else navigate(`/contacts?type=${form.contact_type}`);
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading…</div>;
  }

  const typeLabel = form.contact_type === 'vendor' ? 'Vendor' : 'Customer';

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="contact-form-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={handleBack} data-testid="contact-form-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEdit ? `Edit ${typeLabel}` : `New ${typeLabel}`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Update contact details, phones and addresses.' : `Add a new ${typeLabel.toLowerCase()} with multiple phones and addresses.`}
          </p>
        </div>
      </div>

      {/* Entity Type Toggle (only when creating) */}
      {!isEdit && (
        <Card>
          <CardContent className="p-4">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Entity Type
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, entity_type: 'individual' })}
                className={cn(
                  'flex items-center gap-2 p-3 rounded-xl border-2 transition-all',
                  form.entity_type === 'individual' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                )}
                data-testid="entity-type-individual"
              >
                <UserIcon className={cn('w-5 h-5', form.entity_type === 'individual' ? 'text-primary' : 'text-muted-foreground')} />
                <span className={cn('text-sm font-medium', form.entity_type === 'individual' ? 'text-foreground' : 'text-muted-foreground')}>Individual</span>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, entity_type: 'organization' })}
                className={cn(
                  'flex items-center gap-2 p-3 rounded-xl border-2 transition-all',
                  form.entity_type === 'organization' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                )}
                data-testid="entity-type-organization"
              >
                <Building2 className={cn('w-5 h-5', form.entity_type === 'organization' ? 'text-primary' : 'text-muted-foreground')} />
                <span className={cn('text-sm font-medium', form.entity_type === 'organization' ? 'text-foreground' : 'text-muted-foreground')}>Organization</span>
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Basic info */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {form.entity_type === 'organization' ? 'Company Name' : `${typeLabel} Name`} *
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.contact_type === 'customer' ? 'Acme Corporation' : 'Office Depot'}
              className="h-10"
              data-testid="contact-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contact@company.com"
                className="pl-10 h-10"
                data-testid="contact-email-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phones */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Phone Numbers
            </Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Add work, home, cell, fax or other numbers. Mark one as primary — that's the number shown in lists and PDFs.
          </p>
          <MultiPhoneEditor
            value={form.phones}
            onChange={(phones) => setForm({ ...form, phones })}
          />
        </CardContent>
      </Card>

      {/* Addresses */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Addresses
            </Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Add mailing, billing, shipping or other addresses. Click an address to expand it. The primary address appears on invoices &amp; statements.
          </p>
          <MultiAddressEditor
            value={form.addresses}
            onChange={(addresses) => setForm({ ...form, addresses })}
          />
        </CardContent>
      </Card>

      {/* Terms & credit */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Payment Terms (days)
              </Label>
              <Input
                type="number"
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                className="h-10"
                data-testid="contact-payment-terms-input"
              />
            </div>
            {form.contact_type === 'customer' && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Credit Limit
                </Label>
                <Input
                  type="number"
                  value={form.credit_limit}
                  onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                  placeholder="No limit"
                  className="h-10"
                  data-testid="contact-credit-limit-input"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Optional: apply a folder template to the auto-created root folder */}
      {!isEdit && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FolderTree className="w-4 h-4 text-muted-foreground" />
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Folder Template (optional)
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              A root folder is automatically created for every {typeLabel.toLowerCase()}. Optionally
              pre-fill it with a folder template (e.g., Tax Engagement, Monthly Bookkeeping). You can
              always apply one later from the Documents page.
            </p>
            <Select value={form.apply_template_id || 'none'} onValueChange={(v) => setForm({ ...form, apply_template_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="h-10" data-testid="contact-template-select">
                <SelectValue placeholder="No template — empty root folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template — empty root folder</SelectItem>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} {t.is_system ? '(system)' : ''} · {t.node_count || t.nodes?.length || 0} folders
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-card/95 backdrop-blur border-t px-4 sm:px-0 py-3 flex justify-end gap-3">
        <Button variant="outline" onClick={handleBack} data-testid="contact-form-cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[140px]"
          data-testid="save-contact-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : isEdit ? (
            <Save className="w-4 h-4 mr-2" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-2" />
          )}
          {isEdit ? 'Save Changes' : `Create ${typeLabel}`}
        </Button>
      </div>
    </div>
  );
}
